import { useState, useCallback } from "react";
import Table from "@mui/material/Table";
import { TeamReportDataTableProps } from "./types";
import {
  Snackbar,
  Alert,
  MenuItem,
  Select,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import TeamReportDataRow from "./TeamReportDataRow";
import {
  getReadableRowLabel,
  hasAnyShotDistribution,
  isBelowSampleFloor,
  isSmallSampleRow,
  lowSampleCellNotes,
  volumeUnit,
} from "./utils";
import {
  COLUMN_GROUP_BORDER,
  getColumnGroupStarts,
  SMALL_SAMPLE_POSS_THRESHOLD,
} from "../constants";
import { ShotQueryContext } from "../ShotDetailModal/types";
import { DetailModal } from "../DetailModal";
import { useLeagueApi } from "../../data/LeagueEnvironmentProvider";
import { useLeagueEnvironment } from "../../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../../data/Cache";
import VideoPlayerDialog, { skippedVideoMessage } from "../../components/Elements/VideoPlayerDialog";
import type { VideoPlayerState } from "../../components/Elements/VideoPlayerDialog";

export default function TeamReportDataTable({
  rows,
  headers,
  subRowVariants,
  note,
  hiddenColumns,
  separatorAfterRows,
  disableSmallSampleFade,
  shotDistributionContext,
  hideShotDistribution,
  compactRowLabels,
  hideRowLabels,
  teamId,
  teamAbbrev,
  season,
  seasonType,
  perChancePPP,
  extraNumeralHeaders,
  needleOrdinalColumns,
  smallSampleFloor,
  headerLabels,
  pickTypeRows,
  headerTooltips,
  singleGame,
}: TeamReportDataTableProps) {
  // Presentation-only column hiding — the blob keeps the data, drill-downs
  // and other consumers still see it.
  if (hiddenColumns?.length) {
    headers = headers.filter((h) => !hiddenColumns.includes(h));
  }

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Selectable sub-row breakdowns: the backend advertises the list and orders
  // it so the first variant is the sensible default (also the print
  // promotion). Expansion state survives variant switches — row indexes are
  // stable, and comparing breakdowns of the same expanded row is the point.
  const variantList = subRowVariants ?? [];
  const [activeVariantKey, setActiveVariantKey] = useState<string | null>(null);
  const activeVariant =
    variantList.find((v) => v.key === activeVariantKey) ?? variantList[0];

  // Resolve a row's sub-rows for the active variant; legacy payloads (plain
  // row.subRows, no variant list) fall through unchanged.
  const resolveSubRows = useCallback(
    (row: (typeof rows)[number]) =>
      row.subRowsByType
        ? (activeVariant ? (row.subRowsByType[activeVariant.key] ?? []) : [])
        : (row.subRows ?? []),
    [activeVariant],
  );

  // Build modal title from query context
  const buildTitle = (
    queryContext: ShotQueryContext,
    rowName?: string,
    parentRowName?: string,
  ) => {
    const fmt = (s: string) => s.replace(/_/g, " ");
    const titleParts = [fmt(queryContext.table), fmt(queryContext.section)];
    const isOverall = queryContext.section.toLowerCase() === "overall";
    if (!isOverall) {
      if (parentRowName) titleParts.push(fmt(parentRowName));
      if (rowName) titleParts.push(fmt(rowName));
    }
    return titleParts.join("  \u00BB  ");
  };

  // Shot detail modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    queryContext: ShotQueryContext | null;
    modalTitle: string;
  }>({ isOpen: false, queryContext: null, modalTitle: "" });

  // Handle shot distribution bar click
  const handleShotDistributionClick = useCallback(
    (
      queryContext: ShotQueryContext,
      rowName?: string,
      parentRowName?: string,
    ) => {
      setModalState({
        isOpen: true,
        queryContext,
        modalTitle: buildTitle(queryContext, rowName, parentRowName),
      });
    },
    [],
  );

  // Close modal
  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, queryContext: null, modalTitle: "" });
  }, []);

  // Video playlist state (NBA only)
  const environment = useLeagueEnvironment();
  const api = useLeagueApi();
  const isNBA = environment === ExplorerEnvironment.NBA;
  const [videoLoadingKey, setVideoLoadingKey] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoState, setVideoState] = useState<VideoPlayerState | null>(null);

  const handleVideoPlaylistClick = useCallback(
    async (
      queryContext: ShotQueryContext,
      rowName?: string,
      parentRowName?: string,
      metaKey?: boolean,
    ) => {
      if (!teamId) return;
      setVideoLoadingKey(rowName ?? null);
      setVideoError(null);
      try {
        const response = await api.getVideoPlaylist(
          teamId,
          season,
          queryContext,
        );
        if (response.error) {
          setVideoError(response.error);
        } else if (response.playlistUrl) {
          if (metaKey) {
            setVideoState({
              url: response.playlistUrl,
              title: buildTitle(queryContext, rowName, parentRowName),
              skippedCount: response.skippedCount ?? 0,
            });
          } else {
            window.open(response.playlistUrl, "_blank");
            if (response.skippedCount) {
              setVideoError(skippedVideoMessage(response.skippedCount));
            }
          }
        }
      } catch (err: any) {
        const errorData = err?.response?.data;
        setVideoError(errorData?.error ?? "Failed to generate video playlist");
      } finally {
        setVideoLoadingKey(null);
      }
    },
    [api, teamId, season],
  );

  // Safety checks for missing data
  if (!rows || !headers) {
    return <Typography>No data available</Typography>;
  }

  const showShotDistributionColumn =
    !hideShotDistribution && hasAnyShotDistribution(rows);

  const hasSmallSampleRows =
    !disableSmallSampleFade &&
    rows.some((row) => isSmallSampleRow(row.cells));
  const hasRowsBelowFloor = rows.some((row) =>
    isBelowSampleFloor(row.cells, smallSampleFloor),
  );
  const cellFloorNotes = lowSampleCellNotes(headers, rows);

  // Columns that never carry a pill (e.g. Poss) shouldn't reserve pill space —
  // otherwise the phantom spacer pushes the value off-center.
  const valueOnlyColumns = new Set(
    headers.filter(
      (h) =>
        !rows.some((r) => {
          const c = r.cells?.[h];
          return c && (c.rank !== undefined || c.percentile !== undefined);
        }),
    ),
  );

  const columnGroupStarts = getColumnGroupStarts(headers);

  const rowsWithSubRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => resolveSubRows(row).length > 0);

  const allExpanded = rowsWithSubRows.every(({ index }) =>
    expandedRows.has(index),
  );

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(rowsWithSubRows.map(({ index }) => index)));
    }
  };

  const handleToggledRow = (rowIndex: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  };

  return (
    <>
    <Table
      size="small"
      className="TeamReportDataTable"
      sx={{
        ".MuiTypography-root, .MuiChip-label": {
          fontFamily: '"Roboto Condensed", sans-serif',
        },
        // Personnel table.t grammar (2026-08-25): semibold values, 1.3 leading,
        // visible hairline row borders on tight cells.
        ".MuiTableCell-root .MuiTypography-body1": {
          fontSize: "0.862rem",
          lineHeight: 1.3,
          fontWeight: 600,
        },
        ".MuiTableBody-root .MuiTableCell-root": {
          borderBottom: "1px solid #e6e4e1",
        },
        // Group divider rows (All lead row, coverage family seam): the
        // overview double-rule treatment. Lives here, after the hairline
        // rule, so it wins the specificity race.
        ".MuiTableBody-root .TeamReportSeparatorRow .MuiTableCell-root": {
          borderBottom: "3px double rgba(0, 0, 0, 0.4)",
        },
      }}
    >
      {(hasSmallSampleRows ||
        hasRowsBelowFloor ||
        cellFloorNotes.length > 0 ||
        note) && (
        <caption
          style={{
            captionSide: "bottom",
            textAlign: "right",
            padding: "2px 4px",
          }}
        >
          {note && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "left" }}
            >
              {note}
            </Typography>
          )}
          {hasSmallSampleRows && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              Faded rows: fewer than {SMALL_SAMPLE_POSS_THRESHOLD}{" "}
              {volumeUnit(headers)}
            </Typography>
          )}
          {hasRowsBelowFloor && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "left" }}
            >
              Grey rows: fewer than {smallSampleFloor} {volumeUnit(headers)}{" "}
              (no percentile)
            </Typography>
          )}
          {cellFloorNotes.map((line) => (
            <Typography
              key={line}
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "left" }}
            >
              {line}
            </Typography>
          ))}
        </caption>
      )}
      <TableHead sx={{ ".MuiTableCell-root": { p: 0 } }}>
        <TableRow
          sx={{
            // Report design language: white header row (user call 2026-08-24 —
            // no grey band) over a heavy #4c4c4c rule, condensed uppercase
            // (personnel table.t th grammar, 2026-08-25).
            ".MuiTableCell-root": {
              textAlign: "center",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#4c4c4c",
              borderBottom: "3px solid #4c4c4c",
              px: 0.5,
              fontSize: "0.862rem",
              "@media print": {
                fontSize: "0.805rem",
              },
            },
          }}
        >
          {!hideRowLabels && (
            <TableCell sx={{ px: 0.5 }}>
              {variantList.length > 1 && (
                <Select
                  value={activeVariant?.key ?? ""}
                  onChange={(e) => setActiveVariantKey(e.target.value)}
                  variant="standard"
                  disableUnderline
                  sx={{
                    // Report-native control matching the Expand All toggle:
                    // muted condensed small-caps, no web-app chrome.
                    display: "block",
                    textAlign: "left",
                    ".MuiSelect-select": {
                      p: 0,
                      pr: "18px !important",
                      color: "text.secondary",
                      fontSize: "0.747rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontFamily: '"Roboto Condensed", sans-serif',
                      "&:hover": { color: "text.primary" },
                    },
                    ".MuiSelect-icon": {
                      fontSize: "1.035rem",
                      color: "text.secondary",
                    },
                    "@media print": { display: "none" },
                  }}
                >
                  {variantList.map((v) => (
                    <MenuItem
                      key={v.key}
                      value={v.key}
                      sx={{
                        fontSize: "0.805rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontFamily: '"Roboto Condensed", sans-serif',
                      }}
                    >
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {rowsWithSubRows.length > 0 && (
                <Typography
                  onClick={handleToggleAll}
                  sx={{
                    // Report-native control: muted condensed small-caps, not a
                    // web-app blue link.
                    cursor: "pointer",
                    textAlign: "left",
                    color: "text.secondary",
                    fontSize: "0.747rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    "&:hover": { color: "text.primary" },
                    "@media print": { display: "none" },
                  }}
                >
                  {allExpanded ? "Collapse All" : "Expand All"}
                </Typography>
              )}
            </TableCell>
          )}
          {headers.map((column) => (
            <TableCell
              key={column}
              data-group-start={columnGroupStarts.has(column) || undefined}
              sx={
                columnGroupStarts.has(column)
                  ? { borderLeft: COLUMN_GROUP_BORDER }
                  : undefined
              }
            >
              {headerTooltips?.[column] ? (
                <Tooltip
                  title={headerTooltips[column]}
                  enterDelay={300}
                  placement="top"
                  arrow
                >
                  <span>{headerLabels?.[column] ?? column}</span>
                </Tooltip>
              ) : (
                (headerLabels?.[column] ?? column)
              )}
            </TableCell>
          ))}
          {showShotDistributionColumn && <TableCell>Rim | Mid | 3P</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody
        sx={{
          ".MuiTableRow-root > .MuiTableCell-root:nth-of-type(1) .MuiTypography-root":
            {
              fontWeight: 600,
            },
          ".MuiTableRow-root:hover > .MuiTableCell-root": {
            bgcolor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {rows.map(
          (row, rowIndex) => {
            const {
              cells,
              name,
              shotDistribution,
              shotDistributionContext: rowContext,
              queryContext,
            } = row;
            const subRows = resolveSubRows(row);
            const separatorBelow = Boolean(
              typeof name === "string" && separatorAfterRows?.includes(name),
            );
            const context = rowContext ?? shotDistributionContext;
            const parentLabel = getReadableRowLabel(name, compactRowLabels);
            const parentLabelStr =
              typeof parentLabel === "string" ? parentLabel : undefined;

            const hasSubRows = subRows.length > 0;
            const isExpanded = expandedRows.has(rowIndex);
            // Stripe by logical parent index so a parent and its sub-rows
            // share one stripe and alternation survives expansion.
            const striped = rowIndex % 2 === 1;

            const subRowStyles = {
              visibility: isExpanded ? "visible" : "collapse",
              transition: isExpanded ? "none" : "visibility 0s 0.2s",
              // Print WYSIWYG (user call 2026-08-28): only sub-rows the
              // analyst actively expanded print, inline — the automatic
              // SubRowPrintTable promotion was retired with this.
              "@media print": { display: isExpanded ? undefined : "none" },
              ".MuiTableCell-root": {
                py: isExpanded ? undefined : 0,
                borderColor: isExpanded ? undefined : "transparent",
                overflow: "hidden",
                transition: "all 0.2s ease",
                "> *": {
                  maxHeight: isExpanded ? 100 : 0,
                  opacity: isExpanded ? 1 : 0,
                  transition: "all 0.2s ease",
                },
              },
            };

            return (
              <>
                <TeamReportDataRow
                  key={`row-${rowIndex}`}
                  row={{ name, cells, shotDistribution, queryContext }}
                  headers={headers}
                  separatorBelow={separatorBelow}
                  disableSmallSampleFade={disableSmallSampleFade}
                  isExpanded={isExpanded}
                  striped={striped}
                  valueOnlyColumns={valueOnlyColumns}
                  perChancePPP={perChancePPP}
                  extraNumeralHeaders={extraNumeralHeaders}
                  needleOrdinalColumns={needleOrdinalColumns}
                  smallSampleFloor={smallSampleFloor}
                  subRowStyles={subRowStyles}
                  showShotDistributionColumn={showShotDistributionColumn}
                  shotDistributionContext={context}
                  compactRowLabels={compactRowLabels}
                  hideRowLabels={hideRowLabels}
                  pickTypeRow={pickTypeRows}
                  singleGame={singleGame}
                  onToggle={
                    hasSubRows ? () => handleToggledRow(rowIndex) : undefined
                  }
                  onShotDistributionClick={
                    teamId ? handleShotDistributionClick : undefined
                  }
                  onVideoPlaylistClick={
                    teamId && isNBA ? handleVideoPlaylistClick : undefined
                  }
                  videoLoadingKey={videoLoadingKey}
                />
                {subRows.map((subRow, subIndex) => (
                  <TeamReportDataRow
                    key={`row-${rowIndex}-subrow-${subIndex}`}
                    row={subRow}
                    headers={headers}
                    disableSmallSampleFade={disableSmallSampleFade}
                    isSubRow
                    isExpanded={isExpanded}
                    striped={striped}
                    valueOnlyColumns={valueOnlyColumns}
                    perChancePPP={perChancePPP}
                    extraNumeralHeaders={extraNumeralHeaders}
                    needleOrdinalColumns={needleOrdinalColumns}
                    smallSampleFloor={smallSampleFloor}
                    subRowStyles={subRowStyles}
                    showShotDistributionColumn={showShotDistributionColumn}
                    shotDistributionContext={context}
                    compactRowLabels={compactRowLabels}
                    hideRowLabels={hideRowLabels}
                    parentRowName={parentLabelStr}
                    singleGame={singleGame}
                    onShotDistributionClick={
                      teamId ? handleShotDistributionClick : undefined
                    }
                    onVideoPlaylistClick={
                      teamId && isNBA ? handleVideoPlaylistClick : undefined
                    }
                    videoLoadingKey={videoLoadingKey}
                  />
                ))}
              </>
            );
          },
        )}
      </TableBody>

      {/* Detail Modal (Shot Chart / Action Chart toggle) */}
      {modalState.isOpen && modalState.queryContext && teamId && (
        <DetailModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          queryContext={modalState.queryContext}
          teamId={teamId}
          teamAbbrev={teamAbbrev}
          season={season}
          seasonType={seasonType}
          modalTitle={modalState.modalTitle}
        />
      )}

      {/* Video playlist iframe dialog */}
      <VideoPlayerDialog
        state={videoState}
        onClose={() => setVideoState(null)}
      />

      {/* Video playlist error snackbar */}
      <Snackbar
        open={Boolean(videoError)}
        autoHideDuration={5000}
        onClose={() => setVideoError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setVideoError(null)}
          sx={{ width: "100%" }}
        >
          {videoError}
        </Alert>
      </Snackbar>
    </Table>
    </>
  );
}
